import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

// We never want to route these elements, as they have special behavior
const BLOCKLISTED_PARENT_CLASSES = ['behavior-dupecheck', 'action-remove'];

const isModifiedEvent = (e: React.MouseEvent<any>) =>
    Boolean(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey);

const getNearestAnchor = (
    branch: HTMLElement,
    root: HTMLElement
): HTMLAnchorElement | null | undefined => {
    let candidate = branch;

    while (candidate && candidate.tagName !== 'A') {
        // @ts-expect-error
        candidate = candidate.parentElement === root ? null : candidate.parentElement;
    }

    // Flow is falling down here, so let's cast through any and manually type the expected return;
    return candidate as any;
};

const isSameDomain = (anchor: HTMLAnchorElement) =>
    anchor &&
    window &&
    window.location &&
    anchor.protocol === window.location.protocol &&
    anchor.host === window.location.host;

const hasDataClientRoutable = (anchor: HTMLAnchorElement) => anchor.dataset.clientRoutable === '1';

type Props = {
    children: (arg1: {interceptClicks: (...args: any) => any}) => React.ReactElement;
    history: RouterHistory;
    isClientRoutable: (anchor: HTMLAnchorElement) => boolean;
};

ClientRouteLegacyLinks.defaultProps = {
    // @ts-expect-error
    isClientRoutable: (anchor) => anchor && hasDataClientRoutable(anchor),
};

export function ClientRouteLegacyLinks(props: Props) {
    const interceptClicks = React.useCallback(
        (e: React.MouseEvent<any>) => {
            if (e.defaultPrevented || isModifiedEvent(e)) {
                return;
            }

            if (!(e.target instanceof HTMLElement)) {
                return;
            }

            const anchor = getNearestAnchor(e.target, e.currentTarget);
            if (!anchor || !isSameDomain(anchor)) {
                return;
            }

            const isBlocklistedElement = getIsBlocklisted(e.target);
            if (isBlocklistedElement) {
                return;
            }

            if (!props.isClientRoutable(anchor)) return;

            e.preventDefault();

            // Mark this link as handled by React Router to prevent legacy
            // jQuery interference. See global.js for more details.
            anchor.setAttribute('data-react-router-handled', 'true');

            props.history.push({
                pathname: anchor.pathname,
                search: anchor.search,
                hash: anchor.hash,
            });
            window.scrollTo(0, 0);
        },
        [props]
    );

    return props.children({interceptClicks});
}
/**
 * Recursively traverses up the DOM tree to collect all ancestor elements.
 * This function builds an array of parent elements starting from the immediate parent
 * and continuing up to the root of the document.
 *
 * @param element - The starting HTML element whose parents we want to collect
 * @returns An array of HTMLElements representing the parent hierarchy, ordered from closest to furthest ancestor
 */
function getParents(element: HTMLElement): Array<HTMLElement> {
    const parents = [];
    let current = element.parentElement;

    while (current) {
        parents.push(current);
        current = current.parentElement;
    }

    return parents;
}

/**
 * Determines if an element or any of its ancestors are marked as blocklisted.
 * Currently checks for elements with the 'behavior-dupecheck' class which indicates
 * special handling is required for click events on these elements.
 *
 * Shoutout to Blake for implementing this elegant solution for handling duplicate clicks!
 *
 * @param target - The HTML element to check along with its parent hierarchy
 * @returns true if the target or any ancestor has the blocklist class, false otherwise
 */
function getIsBlocklisted(target: HTMLElement): boolean {
    const elements = [target, ...getParents(target)];
    for (const element of elements) {
        if (
            element.classList &&
            BLOCKLISTED_PARENT_CLASSES.some((className) => element.classList.contains(className))
        ) {
            return true;
        }
    }

    return false;
}
